import { ErrorHandler, inject, NgModule } from '@angular/core'
import { BrowserModule, DomSanitizer } from '@angular/platform-browser'
import { provideRouter } from '@angular/router'
import {
    HttpClient,
    provideHttpClient,
    withInterceptors
} from '@angular/common/http'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'

import { LoadingBarRouterModule } from '@ngx-loading-bar/router'
import { provideTranslateService, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core'
import { CustomMissingTranslationHandler } from '@@shared/services/missing-translation.handler'
import { RetryingTranslateLoader } from '@@shared/services/retrying-translate-loader'
import { MatIconRegistry } from '@angular/material/icon'

import { SharedModule } from '@@shared/shared.module'

import { CompanySettingsFilled } from '@@misc/company-settings-filled'
import { SecurityTagGuard } from '@@misc/security-tag-guard'
import { IsLoggedIn } from '@@misc/is-logged-in'
import { BookingRedirectGuard } from '@@misc/booking-redirect.guard'

import { IconsList } from '@@constants/IconsList'

import { In2SubscriptionService } from '@@services/in2-subscription.service'
import { GoogleLocationService } from '@@services/google-location.service'
import { EventServiceService } from '@@services/event-service.service'
import { NotificationService } from '@@services/notification.service'
import { FingerPrintService } from '@@services/fingerprint.service'
import { WebsocketService } from '@@services/websocket.service'
import { SchedulerService } from '@@services/scheduler.service'
import { SmsEmailsService } from '@@services/smsemail.service'
import { SettingsService } from '@@services/settings.service'
import { TwoWayEncryptor } from '@@services/TwoWayEncryptor'
import { AzureADService } from '@@services/azureAD.service'
import { GeneralService } from '@@services/general.service'
import { FieldsService } from '@@services/fields.service'
import {
    LoaderInterceptorService,
    LoaderService
} from '@@services/loader.service'
import { ToastService } from '@@services/toast.service'
import { AlertService } from '@@services/alert.service'
import { BookService } from '@@services/book.service'
import { AuthService } from '@@services/auth.service'
import { PosService } from '@@services/pos.service'
import { AppService } from '@@services/app.service'
import { BoxService } from '@@services/box.service'
import { DevService } from '@@services/dev.service'
import { RouteData } from '@@services/route-data'

import { AppComponent } from './app.component'
import { AppRoutes } from './app.routing'
import { BackToAppBarComponent } from './layout/back-to-app-bar/back-to-app-bar.component'

import { CastomErrorHandler } from './CastomErrorHandler'
import { PagesModal } from './pages/pages.module'
import { PromoCodeService } from '@@services/promo-code.service'
import { IntegrationService } from '@@services/integration.service'
import { AwsWebSocketService } from '@@services/aws-websocket.service'
import { HubSpotService } from '@@services/hubspot.service'
import { LeadService } from '@@services/lead.service'
import { groupEventsMockInterceptor } from '@@services/group-events-mock.interceptor'
import { environment } from '../environments/environment'
import { LeadImportService } from '@@services/lead-import.service'

const services = [
    AlertService,
    AppService,
    AuthService,
    AzureADService,
    BoxService,
    DevService,
    EventServiceService,
    FingerPrintService,
    GeneralService,
    In2SubscriptionService,
    LoaderService,
    NotificationService,
    SchedulerService,
    SettingsService,
    SmsEmailsService,
    ToastService,
    WebsocketService,
    BookService,
    FieldsService,
    PosService,
    TwoWayEncryptor,
    RouteData,
    GoogleLocationService,
    PromoCodeService,
    LoaderInterceptorService,
    IntegrationService,
    WebsocketService,
    HubSpotService,
    AwsWebSocketService,
    LeadService,
    // Root-level because the import dialog is opened through BoxService (root), whose MatDialog
    // resolves providers from the app injector — a lazy-module provider is invisible to it.
    LeadImportService
]

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        LoadingBarRouterModule,
        SharedModule,
        PagesModal,
        BackToAppBarComponent
    ],
    providers: [
        provideHttpClient(
            withInterceptors([
                // Convert the class-based interceptor to a functional interceptor
                (req, next) => {
                    // Get the LoaderInterceptorService instance from the injector
                    const loaderInterceptor = inject(LoaderInterceptorService)
                    // Call the intercept method
                    return loaderInterceptor.intercept(req, next)
                },
                // DV-5184: opt-in mock for the not-yet-implemented /api/v1/group-events endpoints (environment.mockGroupEvents)
                ...(environment.mockGroupEvents
                    ? [groupEventsMockInterceptor]
                    : [])
            ])
        ),
        provideRouter(AppRoutes),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: (http: HttpClient) =>
                    new RetryingTranslateLoader(http, './i18n/', '.json'),
                deps: [HttpClient]
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: CustomMissingTranslationHandler
            }
        }),
        { provide: ErrorHandler, useClass: CastomErrorHandler },
        CompanySettingsFilled,
        SecurityTagGuard,
        IsLoggedIn,
        BookingRedirectGuard,
        services
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer
    ) {
        Object.entries(IconsList).forEach((entry) => {
            const [key, value] = entry
            value.forEach((icon: string) => {
                this.matIconRegistry.addSvgIcon(
                    icon,
                    this.domSanitizer.bypassSecurityTrustResourceUrl(
                        `/icons/${key}/${icon}.svg`
                    )
                )
            })
        })
    }
}
